import { useEffect } from 'react';

import { IS_DISCORD_ACTIVITY } from '@/globals';

import { VideoPlayerCoreProps, GenericVideoPlayerInterface } from '../videoUtils';

export function twitchLog(msg: string) {
    console.log('[Twitch]', msg);
    (window as any).__twitchLogs = (window as any).__twitchLogs || [];
    (window as any).__twitchLogs.push(`${new Date().toISOString().slice(11, 23)} ${msg}`);
}

function createTwitchPlayerWrapper(): GenericVideoPlayerInterface {
    return {
        getCurrentTime: () => 0,
        getDuration: () => 0,
        isEnded: () => false,
    };
}

function getAncestorHostnames(): string[] {
    try {
        // WebKit-only API: the real chain of ancestor frame origins. Twitch's
        // frame-ancestors check is validated against these, so any origin here
        // that isn't in `parent` causes the frame to be blocked (no onLoad).
        return Array.from(window.location.ancestorOrigins || [])
            .map((origin) => {
                try {
                    return new URL(origin).hostname;
                } catch {
                    return '';
                }
            })
            .filter(Boolean);
    } catch {
        return [];
    }
}

function getTwitchParentDomains(): string[] {
    const domains = new Set<string>([window.location.hostname]);
    getAncestorHostnames().forEach((h) => domains.add(h));
    console.log('jen', window.location.ancestorOrigins)

    if (IS_DISCORD_ACTIVITY) {
        [
            'null', // experiment: try to satisfy the opaque ancestor origin
            'discord.com',
            'discordapp.com',
            'canary.discord.com',
            'ptb.discord.com',
            'discord.gg',
            'discordsays.com',
        ].forEach((d) => domains.add(d));
    }

    return Array.from(domains);
}

export function TwitchPlayerCore({
    videoId: channel,
    className,
    playerRef,
}: VideoPlayerCoreProps) {
    const parent = getTwitchParentDomains();
    const parentParams = parent.map((p) => `parent=${encodeURIComponent(p)}`).join('&');

    // PROXY mode serves the player from our own discordsays.com origin via a
    // Discord URL mapping (prefix /twitch → player.twitch.tv). Toggle at runtime
    // from the debug menu (localStorage flag) — no rebuild needed to flip.
    const useProxy = (() => {
        try {
            return window.localStorage.getItem('ARISE_TWITCH_PROXY') === '1';
        } catch {
            return false;
        }
    })();
    const base = useProxy ? '/.proxy/twitch/' : 'https://player.twitch.tv/';
    const src = `${base}?channel=${encodeURIComponent(channel)}&${parentParams}&autoplay=false&muted=false`;

    twitchLog(`ancestorOrigins: ${JSON.stringify(getAncestorHostnames())} | hostname: ${window.location.hostname}`);
    twitchLog(`parent list: ${JSON.stringify(parent)}`);
    twitchLog(`UA: ${navigator.userAgent}`);
    twitchLog(`mode: ${useProxy ? 'PROXY' : 'DIRECT'} | Rendering iframe | channel: ${channel} | src: ${src}`);

    playerRef.current = createTwitchPlayerWrapper();

    useEffect(() => {
        const onCSPViolation = (e: SecurityPolicyViolationEvent) => {
            twitchLog(`CSP violation: ${e.blockedURI} | directive: ${e.violatedDirective}`);
        };
        document.addEventListener('securitypolicyviolation', onCSPViolation);
        return () => document.removeEventListener('securitypolicyviolation', onCSPViolation);
    }, []);

    return (
        <iframe
            src={src}
            className={className}
            style={{ border: 'none', width: '100%', height: '100%' }}
            allow="autoplay; encrypted-media"
            allowFullScreen={false}
            referrerPolicy="strict-origin-when-cross-origin"
            title="Twitch stream"
            onLoad={() => twitchLog('iframe onLoad fired')}
            onError={() => twitchLog('iframe onError fired')}
        />
    );
}
